/* eslint-disable no-underscore-dangle */
import 'semantic-ui-css/semantic.min.css';

import 'app/styles/style.scss';
import './style.css';

import React from 'react';
import produce from 'immer';
import { pull, reduce, join } from 'lodash';
import { injectIntl, IntlShape } from 'react-intl';

import { getDefaultAnatomicRegion } from 'app/utils/xrayRegions';
import {
  STATEFUL_TOOLS_BUTTON_MAP,
  STATELESS_TOOLS_BUTTON_MAP,
} from 'app/components/ToolsBar/constants';
import { checkOverflowX } from '../../utils/windowUtils';
import ToolsOverflowDropdown from './ToolsOverflowDropdown';
import { ToolsStates } from 'app/adapters/ImageRenderer/ConfigurableToolsOptions';
import { AllPredictions } from 'app/interfaces/Predictions';

type ToolState = 'active' | 'disabled' | 'enabled' | 'passive';

interface ToolDef {
  tool: (...args: unknown[]) => unknown;
  state: ToolState;
  options?: {
    name: string;
    mouseButtonMask?: number;
  };
}

interface ToolsBarOwnProps {
  study?: Record<string, unknown>;
  toolsList: ToolsStates;
  produceCommonToolsList: (fn: (draft: ToolsStates) => void) => void;
  produceImageToolsList: (fn: (draft: ToolsStates) => void) => void;
  displayedToolsLists: string[][];
  toolsProps?: Record<string, Record<string, unknown>>;
  focusedElement?: HTMLElement | null;
  predictions?: AllPredictions;
  anatomicRegion?: string;
  className?: string;
  disabled?: boolean | string[];
  direction?: 'horizontal' | 'vertical';
}

interface ToolsBarProps extends ToolsBarOwnProps {
  intl: IntlShape;
}

interface ToolsBarState {
  collapseToolsBar: boolean;
  overflowingComponents: string[];
}

const insertDividerBetweenTools = (
  toolsListsButtons: React.ReactNode[]
): React.ReactNode[] | null =>
  reduce<React.ReactNode[], React.ReactNode[] | null>(
    toolsListsButtons,
    (result, nextToolsList) => {
      const newResult = [...(result || [])];
      if (result) {
        const lastElement = result[result.length - 1] as React.ReactElement;
        const dividerKey = `${lastElement.key}_divider`;
        newResult.push(<div key={dividerKey} className="tools-divider" />);
      }
      newResult.push(nextToolsList as React.ReactNode);
      return newResult;
    },
    null
  );

class ToolsBar extends React.PureComponent<ToolsBarProps, ToolsBarState> {
  componentsRefs: Map<string, HTMLDivElement | null>;

  lastToolsBarWidth: number | undefined;

  toolsBarRef: HTMLDivElement | undefined;

  constructor(props: ToolsBarProps) {
    super(props);
    this.state = {
      collapseToolsBar: false,
      overflowingComponents: [],
    };
    this.componentsRefs = new Map();
    this.lastToolsBarWidth = undefined;
  }

  componentDidMount(): void {
    this.checkOverflowAndAdjustTools();
  }

  componentDidUpdate(prevProps: ToolsBarProps): void {
    const { focusedElement } = this.props;
    if (prevProps.focusedElement !== focusedElement) {
      this.resetOverFlowingMap();
      return;
    }

    this.checkOverflowAndAdjustTools();
  }

  setToolsBarRef = (el: HTMLDivElement | null): void => {
    if (!el) return;
    this.toolsBarRef = el;
    if (
      !(el as HTMLDivElement & { ToolsBarResizeObserver?: ResizeObserver }).ToolsBarResizeObserver
    ) {
      // eslint-disable-next-line no-param-reassign
      (el as HTMLDivElement & { ToolsBarResizeObserver?: ResizeObserver }).ToolsBarResizeObserver =
        new ResizeObserver(this.handleResize);
      (
        el as HTMLDivElement & { ToolsBarResizeObserver?: ResizeObserver }
      ).ToolsBarResizeObserver!.observe(el);
    }
  };

  setComponentRef = (toolName: string, el: HTMLDivElement | null): void => {
    // This is a pain but we use Map here to preserve insertion order
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
    this.componentsRefs.set(toolName, el);
  };

  handleResize = (): void => this.checkOverflowAndAdjustTools();

  checkOverflowAndAdjustTools = (): void => {
    if (!this.toolsBarRef) return;

    const { x: toolsBarOffsetX, width: toolsBarWidth } = this.toolsBarRef.getBoundingClientRect();
    const isToolsBarGrowing = Math.round(toolsBarWidth) > Math.round(this.lastToolsBarWidth ?? 0);
    this.lastToolsBarWidth = Math.round(toolsBarWidth);

    if (isToolsBarGrowing) {
      this.resetOverFlowingMap();
      return;
    }

    // From here toolsbar is either smaller than before or still has the same size
    const toolsBarEndX = Math.round(toolsBarOffsetX) + Math.round(toolsBarWidth);

    const isOverflow = checkOverflowX(this.toolsBarRef);
    if (isOverflow) {
      this.setState(
        produce((draftState: ToolsBarState) => {
          const { overflowingComponents } = draftState;

          let lastTool: string | undefined;
          this.componentsRefs.forEach((componentRef, toolName) => {
            if (!componentRef) return;
            const { x: toolOffsetX, width } = componentRef.getBoundingClientRect();
            const toolEndX = toolOffsetX + width;
            if (toolEndX >= toolsBarEndX) {
              overflowingComponents.push(toolName);
            }
            lastTool = toolName;
          });

          if (lastTool && !overflowingComponents.includes(lastTool)) {
            overflowingComponents.push(lastTool);
          }
        })
      );
    }
  };

  resetOverFlowingMap = (): void => this.setState({ overflowingComponents: [] });

  collapseTools = (): void =>
    this.setState((state) => ({ collapseToolsBar: !state.collapseToolsBar }));

  getOverflowingTools = (): React.ReactNode[] => {
    const { overflowingComponents } = this.state;
    return pull(
      overflowingComponents.map((toolName) => this.getToolButton(toolName)),
      undefined
    ) as React.ReactNode[];
  };

  getToolButton = (toolName: string): React.ReactNode | undefined => {
    const {
      study,
      toolsList,
      focusedElement,
      predictions,
      produceCommonToolsList,
      produceImageToolsList,
      toolsProps,
      anatomicRegion,
    } = this.props;

    let ToolButton: React.ComponentType<any> =
      STATEFUL_TOOLS_BUTTON_MAP[toolName as keyof typeof STATEFUL_TOOLS_BUTTON_MAP];
    if (ToolButton) {
      const isInToolsList = toolsList[toolName as keyof typeof toolsList] !== undefined;
      if (!isInToolsList) ToolButton = undefined;
    } else {
      ToolButton = STATELESS_TOOLS_BUTTON_MAP[toolName as keyof typeof STATELESS_TOOLS_BUTTON_MAP];
    }
    if (!ToolButton) return undefined;
    return (
      <ToolButton
        name={toolName}
        toolsList={toolsList}
        focusedElement={focusedElement}
        predictions={predictions}
        produceCommonToolsList={produceCommonToolsList}
        produceImageToolsList={produceImageToolsList}
        study={study}
        anatomicRegion={anatomicRegion}
        {...(toolsProps?.[toolName] ?? {})}
      />
    );
  };

  getFilteredToolsList = (displayedToolsList: string[]): React.ReactNode[] | null => {
    const { disabled } = this.props;
    const { overflowingComponents } = this.state;

    return reduce<string, React.ReactNode[] | null>(
      displayedToolsList,
      (result, toolName) => {
        let className = '';
        if (Array.isArray(disabled)) {
          if (disabled.includes(toolName)) {
            className += 'tools-button__disabled';
          }
        }
        if (overflowingComponents.includes(toolName)) return result;

        const toolButton = this.getToolButton(toolName);
        if (!toolButton) return result;

        return [
          ...(result || []),
          <div
            ref={(el) => this.setComponentRef(toolName, el)}
            key={toolName}
            className={className}
          >
            {toolButton}
          </div>,
        ];
      },
      null
    );
  };

  getComponentsToolsList = (): React.ReactNode[] | null => {
    const { displayedToolsLists } = this.props;
    return reduce<string[], React.ReactNode[] | null>(
      displayedToolsLists,
      (result, displayedToolsList) => {
        const buttonsList = this.getFilteredToolsList(displayedToolsList);
        if (!buttonsList) return result;

        const buttonsListKey = join(displayedToolsList, '_');
        return [
          ...(result || []),
          <div key={buttonsListKey} className="tools-group">
            {buttonsList}
          </div>,
        ];
      },
      null
    );
  };

  render(): React.ReactNode {
    const { className, disabled, intl, direction } = this.props;
    const toolsListsButtons = this.getComponentsToolsList();

    if (!toolsListsButtons) return null;
    const dividedToolsListsButtons = insertDividerBetweenTools(toolsListsButtons);

    const overflowingTools = this.getOverflowingTools();
    let toolBarClassName = `tools-bar ${className} icons-${intl.locale}`;
    if (direction === 'vertical') {
      toolBarClassName += ' tools-bar--vertical';
    }
    if (disabled && !Array.isArray(disabled)) {
      toolBarClassName += ' tools-bar__disabled';
    }
    return (
      <div className={toolBarClassName} ref={this.setToolsBarRef}>
        {dividedToolsListsButtons}
        <div
          style={{ display: overflowingTools.length > 0 ? undefined : 'none', maxHeight: '10px' }}
        >
          <ToolsOverflowDropdown tools={overflowingTools} />
        </div>
      </div>
    );
  }
}

export default injectIntl(ToolsBar);
